import React, {useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import {CircularProgress, IconButton, Typography} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DescriptionIcon from "@material-ui/icons/Description";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import {useTranslation} from "react-i18next";
import Flex from "components/grid/Flex";
import PdfViewer from "components/pdf-viewer/PdfViewer";
import * as colors from "components/colors/Colors";

export const ANONYMIZATION_PENDING = "PENDING";
export const ANONYMIZATION_DONE = "DONE";
export const ANONYMIZATION_UNPROCESSABLE = "UNPROCESSABLE";

const BYTES_PER_KILOBYTE = 1024;
const BYTES_PER_MEGABYTE = 1024 * 1024;

const useStyles = makeStyles((theme) => ({
    fileRow: {
        alignItems: "center",
        flexWrap: "nowrap",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 1.5),
        borderRadius: 8,
        background: colors.clinicalSurfaceContainer,
    },
    pendingFileRow: {
        opacity: 0.5,
    },
    unprocessableFileRow: {
        background: colors.clinicalCriticalBackground,
    },
    anonymizedFileRow: {
        cursor: "pointer",
    },
    fileIcon: {
        color: colors.clinicalPrimaryBlue,
    },
    pendingFileIcon: {
        color: colors.clinicalMutedText,
    },
    unprocessableFileIcon: {
        color: colors.clinicalCriticalText,
    },
    fileText: {
        flex: "1 1 auto",
        minWidth: 0,
        flexDirection: "column",
    },
    fileName: {
        fontSize: "0.875rem",
        fontWeight: 600,
        wordBreak: "break-all",
    },
    anonymizedFileName: {
        color: colors.clinicalPrimaryBlue,
    },
    fileDetail: {
        fontSize: "0.75rem",
        fontFamily: "monospace",
        color: colors.clinicalMutedText,
    },
    unprocessableFileDetail: {
        fontSize: "0.75rem",
        color: colors.clinicalCriticalText,
    },
}));

const formatFileSize = (fileSizeInBytes) => {
    if (!Number.isFinite(fileSizeInBytes)) {
        return "";
    }
    if (fileSizeInBytes >= BYTES_PER_MEGABYTE) {
        return `${(fileSizeInBytes / BYTES_PER_MEGABYTE).toFixed(1)} MB`;
    }
    return `${Math.max(1, Math.round(fileSizeInBytes / BYTES_PER_KILOBYTE))} KB`;
};

const ClinicalReportFileIcon = ({anonymizationState, isUnreadable}) => {
    const classes = useStyles();
    if (anonymizationState === ANONYMIZATION_PENDING) {
        return <CircularProgress size={20} className={classes.pendingFileIcon} />;
    }
    if (anonymizationState === ANONYMIZATION_UNPROCESSABLE || isUnreadable) {
        return <ReportProblemIcon className={classes.unprocessableFileIcon} />;
    }
    return <DescriptionIcon className={classes.fileIcon} />;
};

const ClinicalReportFileList = ({clinicalReportFiles, unreadableFileNames, onRemove}) => {
    const classes = useStyles();
    const {t: translate} = useTranslation();
    const [anonymizedPdfUrl, setAnonymizedPdfUrl] = useState(null);

    const openAnonymizedFile = (clinicalReportFile) => {
        if (clinicalReportFile.anonymizationState !== ANONYMIZATION_DONE) {
            return;
        }
        setAnonymizedPdfUrl(URL.createObjectURL(clinicalReportFile.file));
    };

    const closeAnonymizedFile = () => {
        URL.revokeObjectURL(anonymizedPdfUrl);
        setAnonymizedPdfUrl(null);
    };

    const removeClinicalReportFile = (fileRemoveClickEvent, clinicalReportFileId) => {
        fileRemoveClickEvent.stopPropagation();
        onRemove(clinicalReportFileId);
    };

    const isUnreadableFile = (clinicalReportFile) =>
        clinicalReportFile.anonymizationState === ANONYMIZATION_DONE && unreadableFileNames.includes(clinicalReportFile.fileName);

    const fileRowClassName = (clinicalReportFile) => [
        classes.fileRow,
        clinicalReportFile.anonymizationState === ANONYMIZATION_PENDING ? classes.pendingFileRow : "",
        clinicalReportFile.anonymizationState === ANONYMIZATION_DONE ? classes.anonymizedFileRow : "",
        clinicalReportFile.anonymizationState === ANONYMIZATION_UNPROCESSABLE || isUnreadableFile(clinicalReportFile) ? classes.unprocessableFileRow : "",
    ].join(" ");

    return (
        <>
            {clinicalReportFiles.map((clinicalReportFile) => (
                <Flex
                    key={clinicalReportFile.id}
                    container
                    className={fileRowClassName(clinicalReportFile)}
                    onClick={() => openAnonymizedFile(clinicalReportFile)}
                >
                    <ClinicalReportFileIcon
                        anonymizationState={clinicalReportFile.anonymizationState}
                        isUnreadable={isUnreadableFile(clinicalReportFile)}
                    />
                    <Flex container className={classes.fileText}>
                        <Typography
                            className={`${classes.fileName} ${clinicalReportFile.anonymizationState === ANONYMIZATION_DONE ? classes.anonymizedFileName : ""}`}
                        >
                            {clinicalReportFile.fileName}
                        </Typography>
                        {clinicalReportFile.anonymizationState === ANONYMIZATION_PENDING && (
                            <Typography className={classes.fileDetail}>{translate("clinicalReportSummarizer.anonymization.pending")}</Typography>
                        )}
                        {clinicalReportFile.anonymizationState === ANONYMIZATION_DONE && (
                            <Typography className={classes.fileDetail}>{formatFileSize(clinicalReportFile.file?.size)}</Typography>
                        )}
                        {isUnreadableFile(clinicalReportFile) && (
                            <Typography className={classes.unprocessableFileDetail}>
                                {translate("clinicalReportSummarizer.extraction.unreadable")}
                            </Typography>
                        )}
                        {clinicalReportFile.anonymizationState === ANONYMIZATION_UNPROCESSABLE && (
                            <Typography className={classes.unprocessableFileDetail}>
                                {translate("clinicalReportSummarizer.anonymization.unprocessable")}
                            </Typography>
                        )}
                    </Flex>
                    <IconButton
                        size={"small"}
                        onClick={(fileRemoveClickEvent) => removeClinicalReportFile(fileRemoveClickEvent, clinicalReportFile.id)}
                        aria-label={"delete-file"}
                    >
                        <CloseIcon fontSize={"small"} />
                    </IconButton>
                </Flex>
            ))}
            {anonymizedPdfUrl && <PdfViewer fileBlobUrl={anonymizedPdfUrl} onClose={closeAnonymizedFile} />}
        </>
    );
};

export default ClinicalReportFileList;
